import type { KeyboardEvent, ReactNode } from 'react';

export interface RadioCardOption<V extends string> {
  value: V;
  title: ReactNode;
  sub?: ReactNode;
  /** shown at the card's end (an icon) */
  end?: ReactNode;
  /** extra class of this card (its colour when chosen) */
  className?: string;
}

/**
 * Large choice cards that behave as one radio group (WAI-ARIA): one tab stop (the chosen card, else the first),
 * arrow keys move the choice and the focus, Space/Enter or a tap chooses. `describedBy`: the group's error or help.
 */
export function RadioCards<V extends string>({
  label,
  options,
  value,
  onChange,
  describedBy,
  className = '',
}: {
  label: string;
  options: readonly RadioCardOption<V>[];
  value: V | null;
  onChange: (v: V) => void;
  describedBy?: string;
  className?: string;
}) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    if (!step || !options.length) return;
    e.preventDefault();
    const at = options.findIndex((o) => o.value === value);
    const next = options[(at + step + options.length) % options.length].value;
    onChange(next);
    e.currentTarget.querySelector<HTMLElement>(`[data-value="${next}"]`)?.focus();
  };
  const focusable = value ?? options[0]?.value;
  return (
    <div className={className} role="radiogroup" aria-label={label} aria-describedby={describedBy} onKeyDown={onKey}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          data-value={o.value}
          aria-checked={value === o.value}
          tabIndex={o.value === focusable ? 0 : -1}
          className={`radio-card ${o.className ?? ''}`}
          onClick={() => onChange(o.value)}
        >
          <span className="ring" aria-hidden="true" />
          <span className="rc-text">
            <span className="rc-title">{o.title}</span>
            {o.sub && <span className="rc-sub">{o.sub}</span>}
          </span>
          {o.end}
        </button>
      ))}
    </div>
  );
}
